/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a function, not a React component. */
import type { RowData, Table } from '@tanstack/react-table';

import type { DimeTreeTableFeatures } from './tree-table-features.js';

export interface TreeTableDevWarningsOptions<TData extends RowData> {
  table: Table<DimeTreeTableFeatures, TData>;
  indentColumnId: string;
  searchColumn?: string;
  manualFiltering?: boolean;
  onSearchChange?: (value: string) => void;
  onSelectRow?: (node: TData) => void;
  onRowClick?: (node: TData) => void;
}

/**
 * Dev-only console warnings for prop misconfigurations that otherwise fail silently — see
 * DimeTreeTable.md. No-ops entirely outside development builds.
 */
export function logTreeTableDevWarnings<TData extends RowData>({
  table,
  indentColumnId,
  searchColumn,
  manualFiltering,
  onSearchChange,
  onSelectRow,
  onRowClick,
}: Readonly<TreeTableDevWarningsOptions<TData>>): void {
  if (!import.meta.env.DEV) return;

  if (onSelectRow && onRowClick) {
    // eslint-disable-next-line no-console -- intentional dev-only warning, not a stray debug log
    console.warn(
      'DimeTreeTable: onSelectRow and onRowClick are mutually exclusive -- onRowClick will never fire while onSelectRow is set.',
    );
  }

  if (searchColumn && manualFiltering && !onSearchChange) {
    // eslint-disable-next-line no-console -- intentional dev-only warning, not a stray debug log
    console.error(
      'DimeTreeTable: manualFiltering=true with searchColumn but no onSearchChange. ' +
        'Local filtering is disabled — keystrokes call setFilterValue but TanStack ignores them. ' +
        'Pass onSearchChange to route keystrokes to your server query.',
    );
  }

  if (!table.getColumn(indentColumnId)) {
    const availableColumnIds = table
      .getAllLeafColumns()
      .map((column) => column.id)
      .join(', ');
    // eslint-disable-next-line no-console -- intentional dev-only warning, not a stray debug log
    console.warn(
      `DimeTreeTable: indentColumnId="${indentColumnId}" does not match any column id. ` +
        `Available: ${availableColumnIds}`,
    );
  }
}
